import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, yearLabel } from "@/components/shared";
import { Loader2, Briefcase, CheckCircle2, Target, ArrowRight, Award, Calendar, Compass, List, CalendarDays } from "lucide-react";

const BLOC_DOT = {
  "Bloc 2": "bg-orange-500",
  "Bloc 3": "bg-sky-500",
};

const MONTH_NAMES = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function monthLabel(dateStr) {
  const d = new Date(dateStr);
  return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtDayMonth(dateStr) {
  const d = new Date(dateStr);
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()].slice(0, 3)}`;
}

function CalendarView({ year }) {
  const [weeks, setWeeks] = useState(null);

  useEffect(() => {
    api.get("/progression/calendar", { params: { year } }).then(({ data }) => setWeeks(data.weeks));
  }, [year]);

  if (!weeks) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 text-orange-400 animate-spin" /></div>;

  const months = [];
  for (const w of weeks) {
    const label = monthLabel(w.start);
    if (!months.length || months[months.length - 1].label !== label) months.push({ label, weeks: [] });
    months[months.length - 1].weeks.push(w);
  }

  return (
    <div className="mt-8 space-y-5" data-testid="progression-calendar-view">
      <p className="flex items-center gap-1.5 text-xs font-bold text-slate-400">
        <span className="h-2.5 w-2.5 rounded-full bg-slate-200 border border-slate-300" /> Semaines grisées = vacances scolaires (académie de Lille)
      </p>
      {months.map((m) => (
        <div key={m.label} className="rounded-3xl border border-slate-100 bg-white shadow-sm overflow-hidden" data-testid={`cal-month-${m.label.replace(" ", "-")}`}>
          <div className="bg-slate-50 px-5 py-2.5 border-b border-slate-100">
            <p className="font-display text-sm font-bold text-slate-700 capitalize">{m.label}</p>
          </div>
          <div className="divide-y divide-slate-50">
            {m.weeks.map((w) => (
              <div
                key={w.start}
                className={`flex items-center gap-3 px-5 py-3 ${w.holiday ? "bg-slate-50/80" : ""}`}
                data-testid={`cal-week-${w.start}`}
              >
                <span className={`text-xs font-bold whitespace-nowrap w-24 shrink-0 ${w.holiday ? "text-slate-400" : "text-slate-500"}`}>
                  {fmtDayMonth(w.start)} → {fmtDayMonth(w.end)}
                </span>
                {w.holiday ? (
                  <span className="text-xs font-bold text-slate-400 italic">{w.holiday}</span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {w.items.length === 0 && <span className="text-xs text-slate-300 font-medium">—</span>}
                    {w.items.map((it) => (
                      <span
                        key={it.id}
                        className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                          it.type === "pfmp" ? "bg-emerald-100 text-emerald-700" : it.bloc === "Bloc 3" ? "bg-sky-100 text-sky-700" : "bg-orange-100 text-orange-700"
                        }`}
                      >
                        {it.type === "pfmp" && <Briefcase className="h-3 w-3" />}
                        {it.title}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function timelineDotClass(item) {
  if (item.type === "pfmp") return "bg-emerald-500";
  if (item.status === "en_cours") return "bg-orange-500";
  if (item.status === "vue") return "bg-sky-400";
  return "bg-slate-300";
}

function SeanceCard({ item }) {
  const isCurrent = item.status === "en_cours";
  return (
    <div
      className={`rounded-3xl border shadow-sm p-5 sm:p-6 ${isCurrent ? "bg-orange-50 border-orange-200" : "bg-white border-slate-100"}`}
      data-testid={`prog-seance-${item.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[11px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full text-white ${BLOC_DOT[item.bloc] || "bg-slate-400"}`}>{item.bloc}</span>
        <span className="text-[11px] font-extrabold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{item.competency_code}</span>
        {item.period && <span className="text-[11px] font-bold text-slate-400">{item.period}</span>}
        <span className="ml-auto"><StatusBadge status={item.status} /></span>
      </div>
      <h3 className="font-display text-lg font-semibold text-slate-900 mt-2">{item.title}</h3>
      <p className="text-sm text-slate-500 font-medium mt-1">{item.objective}</p>
      <div className="flex items-center justify-between mt-3">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-orange-600"><Target className="h-3.5 w-3.5" /> {item.level_target}</span>
        <Link to="/cours" className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-slate-800">
          Voir le cours <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

function PfmpCard({ item }) {
  const done = item.status === "vue";
  return (
    <div className="rounded-3xl border border-dashed border-emerald-300 bg-emerald-50 p-5 sm:p-6" data-testid={`prog-pfmp-${item.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-600 text-white">
          <Briefcase className="h-3 w-3" /> Stage en entreprise
        </span>
        {item.period && (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold text-emerald-800 bg-white/70 rounded-full px-2.5 py-0.5" data-testid={`pfmp-dates-${item.id}`}>
            <Calendar className="h-3.5 w-3.5" /> {item.period}
          </span>
        )}
        {done && <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-extrabold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Terminée</span>}
      </div>
      <h3 className="font-display text-lg font-semibold text-emerald-900 mt-2">{item.title}</h3>
      <p className="text-sm text-emerald-800/80 font-medium mt-1">{item.objective}</p>
      {item.fiches && (
        <p className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-white/70 rounded-full px-3 py-1 mt-3">
          <Award className="h-3.5 w-3.5" /> À rendre : {item.fiches}
        </p>
      )}
    </div>
  );
}

export default function ProgressionPage() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [view, setView] = useState("liste");

  useEffect(() => {
    api.get("/progression").then(({ data }) => setItems(data.items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!items) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 text-orange-400 animate-spin" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 mz-rise">
      <p className="text-sm font-bold text-orange-500 uppercase tracking-wide">Progression spiralée · {yearLabel(user?.year)}</p>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-1">Progression de l'année</h1>
      <p className="text-slate-500 font-medium mt-1">Toutes tes séquences dans l'ordre, Bloc 2 et Bloc 3 alternés, avec les périodes de stage (PFMP).</p>

      <div className="flex items-center justify-between flex-wrap gap-3 mt-5">
        <div className="flex flex-wrap gap-3">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600"><span className="h-2.5 w-2.5 rounded-full bg-orange-500" /> Bloc 2</span>
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600"><span className="h-2.5 w-2.5 rounded-full bg-sky-500" /> Bloc 3</span>
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> PFMP (stage)</span>
        </div>
        <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="progression-view-toggle">
          <button
            type="button"
            onClick={() => setView("liste")}
            data-testid="progression-view-liste-btn"
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${view === "liste" ? "bg-white text-orange-600 shadow-sm" : "text-slate-500"}`}
          >
            <List className="h-3.5 w-3.5" /> Liste
          </button>
          <button
            type="button"
            onClick={() => setView("calendrier")}
            data-testid="progression-view-calendrier-btn"
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${view === "calendrier" ? "bg-white text-orange-600 shadow-sm" : "text-slate-500"}`}
          >
            <CalendarDays className="h-3.5 w-3.5" /> Calendrier
          </button>
        </div>
      </div>

      <Link to="/pfmp-univers" data-testid="pfmp-univers-cta" className="group mt-6 flex items-center gap-4 rounded-3xl bg-gradient-to-br from-emerald-500 to-sky-500 text-white p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow">
        <div className="h-12 w-12 rounded-2xl bg-white/20 flex items-center justify-center shrink-0"><Compass className="h-6 w-6" /></div>
        <div className="flex-1">
          <p className="font-display text-lg font-semibold leading-tight">Où faire ma PFMP ?</p>
          <p className="text-sm text-white/85 font-medium mt-0.5">Explore les 11 univers commerciaux et les métiers accessibles avec ton CAP.</p>
        </div>
        <ArrowRight className="h-5 w-5 shrink-0 group-hover:translate-x-1 transition-transform" />
      </Link>

      {view === "calendrier" ? (
        <CalendarView year={user?.year} />
      ) : (
        <div className="relative mt-8 pl-7 sm:pl-9">
          <div className="absolute left-2.5 sm:left-3.5 top-2 bottom-2 w-0.5 bg-slate-200" />
          <div className="space-y-5">
            {items.map((item, i) => (
              <div key={item.id} className="relative">
                <span
                  className={`absolute -left-[22px] sm:-left-[26px] top-5 h-3.5 w-3.5 rounded-full ring-4 ring-white ${timelineDotClass(item)}`}
                  data-testid={`prog-dot-${i}`}
                />
                {item.type === "pfmp" ? <PfmpCard item={item} /> : <SeanceCard item={item} />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
